import { useCallback } from 'react'
import { GridApi } from 'ag-grid-community'
import { Dependency, useDependencyContext } from './DependencyContext'
import { useDependencyDragInteraction } from './useDependencyDragInteraction'
import { DepPopupState, useDependencyLinePopup } from './useDependencyLinePopup'
import { categorizeLinks, FallbackLinkInfo } from '../utils/visibilityFilters'
import {
  getWbsItemDisplayName,
  getWbsItemIconUrl,
} from '../utils/linkInfoPopupUtils'
import { LinkEditorResult } from '../WbsItemLink/LinkEditorPopup'

type UseGanttDependencyInteractionParams = {
  api: GridApi
  dataPath?: string
  rowIndex: number
  isTask: boolean
  isDeliverable: boolean
  barEndpoints: { startX: number | null; endX: number | null }
  isHovered: boolean
  isRowHovered: boolean
}

type UseGanttDependencyInteractionResult = {
  dependencies: Dependency[]
  fallbackDeps: FallbackLinkInfo<Dependency>[]
  isDependencyDragging: boolean
  isPotentialDropTarget: boolean
  showLeftDropTarget: boolean
  showSourceCircles: boolean
  handleConnectionMouseDown: (
    event: React.MouseEvent,
    side: 'start' | 'end'
  ) => void
  handleDropTargetMouseUp: (event: React.MouseEvent) => void
  depPopup: DepPopupState | null
  handleDependencyLineClick: (dep: Dependency, event: React.MouseEvent) => void
  handleDeleteDependency: () => void
  handleDependencyUpdate: (result: LinkEditorResult) => void
  closeDependencyPopup: () => void
  handleFallbackDepClick: (
    info: FallbackLinkInfo<Dependency>,
    event: React.MouseEvent
  ) => void
}

export const useGanttDependencyInteraction = (
  params: UseGanttDependencyInteractionParams
): UseGanttDependencyInteractionResult => {
  const {
    api,
    dataPath,
    rowIndex,
    isTask,
    isDeliverable,
    barEndpoints,
    isHovered,
    isRowHovered,
  } = params
  const prefix = dataPath ? `${dataPath}.` : ''

  // Dependency context: categorize into visible lines and fallback icons
  const dependencyContext = useDependencyContext()
  const { visible: dependencies, fallback: fallbackDeps } = categorizeLinks(
    dependencyContext?.dependencies ?? [],
    api
  )

  // Dependency item type for this row
  const dependencyItemType = isTask
    ? 'task'
    : isDeliverable
      ? 'deliverable'
      : null

  // Dependency drag interaction
  const {
    isDependencyDragging,
    isPotentialDropTarget,
    showLeftDropTarget,
    showSourceCircles,
    handleConnectionMouseDown,
    handleDropTargetMouseUp,
  } = useDependencyDragInteraction({
    rowIndex,
    dependencyItemType,
    barEndpoints,
    isHovered,
    isRowHovered,
  })

  // Dependency line popup
  const {
    depPopup,
    handleLineClick: handleDependencyLineClick,
    handleDelete: handleDeleteDependency,
    handleUpdate: handleDependencyUpdate,
    closePopup: closeDependencyPopup,
    openPopup: openDepPopup,
  } = useDependencyLinePopup(api, dataPath)

  // WBS item info map from context (server-sourced, avoids full-grid iteration)
  const depWbsItemInfoMap = dependencyContext?.wbsItemInfoMap

  // Fallback icon click handler (resolve names/icons for hidden endpoints)
  const handleFallbackDepClick = useCallback(
    (info: FallbackLinkInfo<Dependency>, event: React.MouseEvent) => {
      const { link, from, to } = info
      let fromName: string
      let fromIconUrl: string | undefined
      if (from.isFallback) {
        const resolved = depWbsItemInfoMap?.get(link.fromWbsItemUuid)
        fromName = resolved?.displayName ?? ''
        fromIconUrl = resolved?.iconUrl
      } else {
        fromName = getWbsItemDisplayName(api, from.rowIndex, prefix)
        fromIconUrl = getWbsItemIconUrl(api, from.rowIndex, prefix)
      }
      let toName: string
      let toIconUrl: string | undefined
      if (to.isFallback) {
        const resolved = depWbsItemInfoMap?.get(link.toWbsItemUuid)
        toName = resolved?.displayName ?? ''
        toIconUrl = resolved?.iconUrl
      } else {
        toName = getWbsItemDisplayName(api, to.rowIndex, prefix)
        toIconUrl = getWbsItemIconUrl(api, to.rowIndex, prefix)
      }
      openDepPopup({
        dependency: link,
        fromName,
        toName,
        fromIconUrl,
        toIconUrl,
      })
    },
    [api, prefix, openDepPopup, depWbsItemInfoMap]
  )

  return {
    dependencies,
    fallbackDeps,
    isDependencyDragging,
    isPotentialDropTarget,
    showLeftDropTarget,
    showSourceCircles,
    handleConnectionMouseDown,
    handleDropTargetMouseUp,
    depPopup,
    handleDependencyLineClick,
    handleDeleteDependency,
    handleDependencyUpdate,
    closeDependencyPopup,
    handleFallbackDepClick,
  }
}
